import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../auth/AuthContext';

export default function AdminDashboardProjects() {
  const { csrfToken } = useAuth();

  const [projects, setProjects] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);

  const apiBase = process.env.REACT_APP_API_URL || '';

  const [form, setForm] = useState({
    title: '',
    description: '',
    techStack: '',
    githubUrl: '',
    demoUrl: '',
    role: '',
    challenges: '',
    solutions: '',
    features: '',
    category: '',
    tools: '',
    coverImage: null,           // { url, publicId } | null
    galleryImages: [],          // [{ url, publicId }]
    videos: '',
    order: 0
  });

  const loadProjects = async () => {
    try {
      const { data } = await axios.get(`${apiBase}/api/projects`, {
        withCredentials: true
      });
      setProjects(data);
    } catch (err) {
      console.error('Error loading projects:', err);
      alert('Error loading projects');
    }
  };

  useEffect(() => {
    loadProjects();
  }, []);

  const resetForm = () => {
    setForm({
      title: '',
      description: '',
      techStack: '',
      githubUrl: '',
      demoUrl: '',
      role: '',
      challenges: '',
      solutions: '',
      features: '',
      category: '',
      tools: '',
      coverImage: null,
      galleryImages: [],
      videos: '',
      order: 0
    });
    setEditingId(null);
  };

  const splitToArray = value =>
    value
      ? value
          .split(',')
          .map(s => s.trim())
          .filter(s => s.length > 0)
      : [];

  const submitForm = async e => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        title: form.title,
        description: form.description,
        techStack: splitToArray(form.techStack),
        githubUrl: form.githubUrl,
        demoUrl: form.demoUrl,
        role: form.role,
        challenges: form.challenges,
        solutions: form.solutions,
        features: splitToArray(form.features),
        category: form.category,
        tools: splitToArray(form.tools),
        coverImage: form.coverImage,          // object or null
        galleryImages: form.galleryImages,    // array of objects
        videos: splitToArray(form.videos),
        order: Number(form.order) || 0
      };

      if (editingId) {
        await axios.put(`${apiBase}/api/projects/${editingId}`, payload, {
          headers: { 'X-CSRF-Token': csrfToken },
          withCredentials: true
        });
      } else {
        await axios.post(`${apiBase}/api/projects`, payload, {
          headers: { 'X-CSRF-Token': csrfToken },
          withCredentials: true
        });
      }

      await loadProjects();
      resetForm();
    } catch (err) {
      console.error('Error saving project:', err);
      alert('Error saving project');
    } finally {
      setLoading(false);
    }
  };

  const deleteProject = async id => {
    if (!window.confirm('Delete this project?')) return;

    try {
      await axios.delete(`${apiBase}/api/projects/${id}`, {
        headers: { 'X-CSRF-Token': csrfToken },
        withCredentials: true
      });
      await loadProjects();
    } catch (err) {
      console.error('Error deleting project:', err);
      alert('Error deleting project');
    }
  };

  const startEdit = p => {
    setEditingId(p._id);
    setForm({
      title: p.title || '',
      description: p.description || '',
      techStack: (p.techStack || []).join(', '),
      githubUrl: p.githubUrl || '',
      demoUrl: p.demoUrl || '',
      role: p.role || '',
      challenges: p.challenges || '',
      solutions: p.solutions || '',
      features: (p.features || []).join(', '),
      category: p.category || '',
      tools: (p.tools || []).join(', '),
      coverImage: p.coverImage || null,
      galleryImages: p.galleryImages || [],
      videos: (p.videos || []).join(', '),
      order: p.order || 0
    });
  };

  const uploadCover = async e => {
    const file = e.target.files[0];
    if (!file) return;

    const fd = new FormData();
    fd.append('image', file);

    try {
      const { data } = await axios.post(
        `${apiBase}/api/upload/project-cover`,
        fd,
        { withCredentials: true }
      );

      setForm(prev => ({
        ...prev,
        coverImage: {
          url: data.url,
          publicId: data.publicId
        }
      }));
    } catch (err) {
      console.error('Error uploading cover image:', err);
      alert('Error uploading cover image');
    }
  };

  const uploadGallery = async e => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const fd = new FormData();
    for (let f of files) fd.append('images', f);

    try {
      const { data } = await axios.post(
        `${apiBase}/api/upload/project-gallery`,
        fd,
        { withCredentials: true }
      );

      // data.images: [{ url, publicId }]
      setForm(prev => ({
        ...prev,
        galleryImages: [...prev.galleryImages, ...data.images]
      }));
    } catch (err) {
      console.error('Error uploading gallery images:', err);
      alert('Error uploading gallery images');
    }
  };

  const removeGalleryImage = img => {
    setForm(prev => ({
      ...prev,
      galleryImages: prev.galleryImages.filter(
        g => g.publicId !== img.publicId
      )
    }));
    // اینجا می‌توانیم بعداً یک endpoint جدا برای حذف تکی هم اضافه کنیم اگر خواستی
  };

  return (
    <div className="container py-5">
      <h1 className="h4 mb-4">
        {editingId ? 'Edit Project' : 'Add New Project'}
      </h1>

      <form onSubmit={submitForm} className="mb-4">
        <div className="row g-3">
          {/* Title */}
          <div className="col-md-6">
            <label className="form-label">Title</label>
            <input
              className="form-control"
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
              required
            />
          </div>

          {/* Order */}
          <div className="col-md-2">
            <label className="form-label">Order</label>
            <input
              type="number"
              className="form-control"
              value={form.order}
              onChange={e => setForm({ ...form, order: e.target.value })}
            />
          </div>

          {/* Category */}
          <div className="col-md-4">
            <label className="form-label">Category</label>
            <input
              className="form-control"
              value={form.category}
              onChange={e => setForm({ ...form, category: e.target.value })}
              placeholder="Web App, Mobile App, AI, ..."
            />
          </div>

          {/* Description */}
          <div className="col-12">
            <label className="form-label">Description</label>
            <textarea
              className="form-control"
              rows={3}
              value={form.description}
              onChange={e =>
                setForm({ ...form, description: e.target.value })
              }
              required
            />
          </div>

          {/* Role */}
          <div className="col-md-4">
            <label className="form-label">Your Role</label>
            <input
              className="form-control"
              value={form.role}
              onChange={e => setForm({ ...form, role: e.target.value })}
              placeholder="Full-stack Developer, Backend Engineer, ..."
            />
          </div>

          {/* Tech Stack */}
          <div className="col-md-4">
            <label className="form-label">Tech Stack</label>
            <input
              className="form-control"
              value={form.techStack}
              onChange={e =>
                setForm({ ...form, techStack: e.target.value })
              }
              placeholder="React, Node.js, MongoDB"
            />
          </div>

          {/* Tools */}
          <div className="col-md-4">
            <label className="form-label">Tools</label>
            <input
              className="form-control"
              value={form.tools}
              onChange={e => setForm({ ...form, tools: e.target.value })}
              placeholder="Jira, Figma, Docker"
            />
          </div>

          {/* Challenges */}
          <div className="col-md-6">
            <label className="form-label">Challenges</label>
            <textarea
              className="form-control"
              rows={3}
              value={form.challenges}
              onChange={e =>
                setForm({ ...form, challenges: e.target.value })
              }
            />
          </div>

          {/* Solutions */}
          <div className="col-md-6">
            <label className="form-label">Solutions</label>
            <textarea
              className="form-control"
              rows={3}
              value={form.solutions}
              onChange={e =>
                setForm({ ...form, solutions: e.target.value })
              }
            />
          </div>

          {/* Features */}
          <div className="col-12">
            <label className="form-label">Features</label>
            <input
              className="form-control"
              value={form.features}
              onChange={e => setForm({ ...form, features: e.target.value })}
              placeholder="Authentication, RBAC, Analytics"
            />
          </div>

          {/* GitHub */}
          <div className="col-md-6">
            <label className="form-label">GitHub URL</label>
            <input
              className="form-control"
              value={form.githubUrl}
              onChange={e =>
                setForm({ ...form, githubUrl: e.target.value })
              }
            />
          </div>

          {/* Demo */}
          <div className="col-md-6">
            <label className="form-label">Live Demo URL</label>
            <input
              className="form-control"
              value={form.demoUrl}
              onChange={e => setForm({ ...form, demoUrl: e.target.value })}
            />
          </div>

          {/* Cover Image */}
          <div className="col-md-6">
            <label className="form-label">Cover Image</label>
            <input type="file" className="form-control" onChange={uploadCover} />
            {form.coverImage?.url && (
              <img
                src={form.coverImage.url}
                alt="cover"
                className="mt-2 rounded border"
                style={{ width: '120px', height: '80px', objectFit: 'cover' }}
              />
            )}
          </div>

          {/* Gallery */}
          <div className="col-md-6">
            <label className="form-label">Gallery Images</label>
            <input
              type="file"
              multiple
              className="form-control"
              onChange={uploadGallery}
            />

            {form.galleryImages.length > 0 && (
              <div className="mt-2 d-flex flex-wrap gap-2">
                {form.galleryImages.map(img => (
                  <div key={img.publicId} style={{ position: 'relative' }}>
                    <img
                      src={img.url}
                      alt=""
                      className="rounded border"
                      style={{
                        width: '80px',
                        height: '80px',
                        objectFit: 'cover'
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      style={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        borderRadius: '50%',
                        padding: '0 6px'
                      }}
                      onClick={() => removeGalleryImage(img)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Videos */}
          <div className="col-12">
            <label className="form-label">Videos (URLs)</label>
            <input
              className="form-control"
              value={form.videos}
              onChange={e => setForm({ ...form, videos: e.target.value })}
              placeholder="https://youtube.com/... , https://..."
            />
          </div>
        </div>

        <button className="btn btn-primary mt-3" disabled={loading}>
          {loading
            ? 'Saving...'
            : editingId
            ? 'Update Project'
            : 'Add Project'}
        </button>

        {editingId && (
          <button
            type="button"
            className="btn btn-secondary mt-3 ms-2"
            onClick={resetForm}
          >
            Cancel
          </button>
        )}
      </form>

      {/* Table */}
      <h2 className="h5 mb-3">Existing Projects</h2>

      <div className="table-responsive">
        <table className="table table-bordered align-middle">
          <thead>
            <tr>
              <th>Order</th>
              <th>Title</th>
              <th>Category</th>
              <th>Role</th>
              <th>Cover</th>
              <th>Tech Stack</th>
              <th>Features</th>
              <th>Videos</th>
              <th style={{ width: '160px' }}>Actions</th>
            </tr>
          </thead>

          <tbody>
            {projects.map(p => (
              <tr key={p._id}>
                <td>{p.order}</td>
                <td>{p.title}</td>
                <td>{p.category}</td>
                <td>{p.role}</td>

                <td>
                  {p.coverImage?.url && (
                    <img
                      src={p.coverImage.url}
                      alt=""
                      style={{
                        width: '80px',
                        height: '50px',
                        objectFit: 'cover',
                        borderRadius: '4px'
                      }}
                    />
                  )}
                </td>

                <td>{(p.techStack || []).join(', ')}</td>
                <td>{(p.features || []).join(', ')}</td>
                <td>{(p.videos || []).length}</td>

                <td>
                  <button
                    className="btn btn-sm btn-warning me-2"
                    onClick={() => startEdit(p)}
                  >
                    Edit
                  </button>

                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => deleteProject(p._id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}

            {projects.length === 0 && (
              <tr>
                <td colSpan="9" className="text-center text-muted">
                  No projects found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
